import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api'
import { ConfirmModal, SuccessModal } from '../../components/ui/Modal'

interface PendingComment {
  id: number
  opportunity_id: number
  content: string
  created_at: string
  opportunity_title: string
  subreddit: string
  post_url: string
  project_name: string
  project_id: number
  author?: string
}

interface SuccessInfo {
  message: string
  scheduledTime?: string
  subreddit?: string
  isRejection?: boolean
}

export default function PendingApprovalsPage() {
  const queryClient = useQueryClient()
  const [comments, setComments] = useState<PendingComment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProject, setSelectedProject] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [processingId, setProcessingId] = useState<number | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [timingStrategy, setTimingStrategy] = useState<'optimal' | 'immediate' | 'random' | 'custom'>('optimal')

  // Modal states
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [commentToReject, setCommentToReject] = useState<number | null>(null)
  const [successModalOpen, setSuccessModalOpen] = useState(false)
  const [successInfo, setSuccessInfo] = useState<SuccessInfo | null>(null)

  useEffect(() => {
    loadPendingApprovals()
  }, [page, selectedProject])

  const loadPendingApprovals = async () => {
    try {
      setLoading(true)
      setError(null)

      const filters: any = { page, per_page: 20 }
      if (selectedProject) filters.project_id = selectedProject

      const response = await apiClient.getPendingApprovals(filters)

      if (response.success) {
        // Backend returns flat structure with 'items' instead of nested 'data'/'pagination'
        const responseData = response.data || response as any
        setComments(responseData.items || responseData.data || [])
        setTotalPages(responseData.total_pages || 1)
      } else {
        setError('Failed to load pending approvals')
      }
    } catch (err: any) {
      console.error('Error loading pending approvals:', err)
      setError(err.message || 'Failed to load pending approvals')
    } finally {
      setLoading(false)
    }
  }

  const handleApprove = async (commentId: number) => {
    try {
      setProcessingId(commentId)
      setError(null)

      const comment = comments.find(c => c.id === commentId)
      const response = await apiClient.approveComment(commentId, timingStrategy)

      if (response.success) {
        // Remove from list
        setComments(prev => prev.filter(c => c.id !== commentId))

        // Invalidate React Query caches so dashboard updates
        queryClient.invalidateQueries({ queryKey: ['automationStats'] })
        queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] })

        // Show success modal
        setSuccessInfo({
          message: 'Comment approved and scheduled successfully!',
          scheduledTime: response.data.schedule?.scheduled_time_formatted || new Date(response.data.scheduled_time).toLocaleString(),
          subreddit: comment?.subreddit,
          isRejection: false
        })
        setSuccessModalOpen(true)
      } else {
        setError('Failed to approve comment')
      }
    } catch (err: any) {
      console.error('Error approving comment:', err)
      setError(err.message || 'Failed to approve comment')
    } finally {
      setProcessingId(null)
    }
  }

  const openRejectModal = (commentId: number) => {
    setCommentToReject(commentId)
    setRejectModalOpen(true)
  }

  const confirmReject = async () => {
    if (!commentToReject) return

    try {
      setProcessingId(commentToReject)
      setError(null)

      const response = await apiClient.rejectComment(commentToReject, 'Rejected by user')

      if (response.success) {
        // Close reject modal first
        setRejectModalOpen(false)

        // Remove from list
        setComments(prev => prev.filter(c => c.id !== commentToReject))

        // Invalidate React Query caches so dashboard updates
        queryClient.invalidateQueries({ queryKey: ['automationStats'] })
        queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] })

        // Show success modal
        setSuccessInfo({
          message: 'Comment rejected successfully',
          isRejection: true
        })
        setSuccessModalOpen(true)
      } else {
        setError('Failed to reject comment')
      }
    } catch (err: any) {
      console.error('Error rejecting comment:', err)
      setError(err.message || 'Failed to reject comment')
    } finally {
      setProcessingId(null)
      setCommentToReject(null)
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              👀 Pending Approvals
            </h1>
            <p className="text-gray-600 mt-2">
              Review and approve auto-generated comments before they're posted to Reddit
            </p>
          </div>

          <div className="text-right">
            <div className="text-3xl font-bold text-blue-600">
              {comments.length}
            </div>
            <div className="text-sm text-gray-600">
              Comments Pending
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Empty State */}
      {!loading && comments.length === 0 && (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            All Caught Up!
          </h2>
          <p className="text-gray-600 mb-6">
            No comments pending approval right now. Auto-generated comments will appear here when they need your review.
          </p>
          <Link
            to="/opportunities"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
          >
            View Opportunities
          </Link>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-lg shadow p-6 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-3"></div>
              <div className="h-20 bg-gray-200 rounded mb-4"></div>
              <div className="flex space-x-2">
                <div className="h-10 bg-gray-200 rounded w-24"></div>
                <div className="h-10 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Comments List */}
      {!loading && comments.length > 0 && (
        <div className="space-y-4">
          {comments.map(comment => (
            <div
              key={comment.id}
              className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              <div className="p-6">
                {/* Opportunity Info */}
                <div className="mb-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {comment.opportunity_title}
                      </h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        <span className="flex items-center">
                          <span className="font-semibold text-orange-600 mr-1">
                            r/{comment.subreddit}
                          </span>
                        </span>
                        {comment.author && <span>by u/{comment.author}</span>}
                        <span className="text-blue-600 font-medium">
                          {comment.project_name}
                        </span>
                      </div>
                    </div>

                    <a
                      href={comment.post_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
                    >
                      View on Reddit →
                    </a>
                  </div>
                </div>

                {/* Generated Comment */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-semibold text-gray-700">
                      🤖 Auto-Generated Comment:
                    </label>
                    <span className="text-xs text-gray-500">
                      Generated {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>

                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-gray-800 whitespace-pre-wrap">
                      {comment.content}
                    </p>
                  </div>

                  <div className="mt-2 text-xs text-gray-500">
                    {comment.content.length} characters • ~{Math.ceil(comment.content.split(' ').length / 200)} min read
                  </div>
                </div>

                {/* Timing Strategy Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    ⏰ When to Post:
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setTimingStrategy('optimal')}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        timingStrategy === 'optimal'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      🎯 Optimal Time
                    </button>
                    <button
                      onClick={() => setTimingStrategy('immediate')}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        timingStrategy === 'immediate'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      ⚡ Post Soon
                    </button>
                    <button
                      onClick={() => setTimingStrategy('random')}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        timingStrategy === 'random'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      🎲 Random Time
                    </button>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleApprove(comment.id)}
                    disabled={processingId === comment.id}
                    className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {processingId === comment.id ? (
                      'Processing...'
                    ) : (
                      '✅ Approve & Schedule'
                    )}
                  </button>

                  <button
                    onClick={() => openRejectModal(comment.id)}
                    disabled={processingId === comment.id}
                    className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    ❌ Reject
                  </button>

                  <button
                    onClick={() => setExpandedId(expandedId === comment.id ? null : comment.id)}
                    className="px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
                  >
                    📝 Edit
                  </button>
                </div>

                {/* Edit Mode (Collapsed by Default) */}
                {expandedId === comment.id && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Edit Comment Before Approval:
                    </label>
                    <textarea
                      value={comment.content}
                      onChange={(e) => {
                        const newComments = comments.map(c =>
                          c.id === comment.id ? { ...c, content: e.target.value } : c
                        )
                        setComments(newComments)
                      }}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="mt-2 text-xs text-gray-600">
                      💡 Edit the comment to improve quality or adjust tone before approving
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-8 flex items-center justify-center space-x-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>

          <span className="px-4 py-2 text-gray-700">
            Page {page} of {totalPages}
          </span>

          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">
          💡 How Review Mode Works
        </h3>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            <strong>1.</strong> System auto-generates comments for high-quality opportunities
          </p>
          <p>
            <strong>2.</strong> Comments appear here for your review and approval
          </p>
          <p>
            <strong>3.</strong> You can edit, approve with timing strategy, or reject
          </p>
          <p>
            <strong>4.</strong> Approved comments are scheduled and posted automatically
          </p>
          <p className="mt-4 pt-4 border-t border-blue-300">
            <strong>⚙️ Timing Strategies:</strong>
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Optimal Time:</strong> AI calculates best time based on subreddit activity patterns</li>
            <li><strong>Post Soon:</strong> Schedules within 5-15 minutes (with random delay)</li>
            <li><strong>Random Time:</strong> Random time within active hours for natural behavior</li>
          </ul>
        </div>
      </div>

      {/* Reject Confirmation Modal */}
      <ConfirmModal
        isOpen={rejectModalOpen}
        onClose={() => {
          setRejectModalOpen(false)
          setCommentToReject(null)
        }}
        onConfirm={confirmReject}
        title="Reject Comment?"
        message="Are you sure you want to reject this auto-generated comment? This action cannot be undone."
        confirmText="Yes, Reject"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        icon="🗑️"
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={successModalOpen}
        onClose={() => {
          setSuccessModalOpen(false)
          setSuccessInfo(null)
        }}
        title={successInfo?.isRejection ? 'Comment Rejected' : 'Comment Approved!'}
        message={successInfo?.message || ''}
        icon={successInfo?.isRejection ? '❌' : '✅'}
        details={
          !successInfo?.isRejection && successInfo?.scheduledTime ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">Scheduled For:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {successInfo.scheduledTime}
                </span>
              </div>
              {successInfo.subreddit && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-600">Subreddit:</span>
                  <span className="text-sm font-semibold text-orange-600">
                    r/{successInfo.subreddit}
                  </span>
                </div>
              )}
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-600 text-center">
                  💡 The comment will be posted automatically at the scheduled time
                </p>
              </div>
            </div>
          ) : undefined
        }
        buttonText={successInfo?.isRejection ? 'Close' : 'Great!'}
      />
    </div>
  )
}
